/**
 * ขยับโมเดลตัวละคร .glb ที่ใส่กระดูกแล้ว (ชื่อกระดูกแบบ Mixamo จาก tools/autorig.py)
 *
 * ไม่มีไฟล์ท่าทาง — หมุนกระดูกเองจากสถานะเดียวกับที่ `animateChibi` ใช้ (เดินไหม เวลา จังหวะตี)
 *
 * **คณิตทั้งหมดคิดใน "พื้นที่ glTF"** (Y ขึ้น, หน้าตัวละคร +Z, ขวาของตัวละคร = -X)
 * ไม่ใช่พื้นที่โลกของ Babylon เพราะ `__root__` ที่ตัวโหลดใส่ไว้มี scale z = -1 (แปลงมือขวา→ซ้าย)
 * ถ้าหมุนในพื้นที่โลกตรง ๆ ทิศจะกลับด้านแบบเดาไม่ได้
 * ควอเทอร์เนียนของ node ใต้ `__root__` เป็นค่าจากไฟล์ตรง ๆ จึงคูณแบบ Hamilton ของ glTF ได้เลย
 *
 * วิธีใส่ท่า: แต่ละกระดูกมี "การหมุนเพิ่ม" D ที่บอกในพื้นที่โมเดลตอนท่าตั้งต้น
 * local ใหม่ = inv(RW_parent) · D · RW_self  (RW = การหมุนสะสมจากรากถึงกระดูกตอนท่าตั้งต้น)
 * สูตรนี้ทำให้ลูกขยับตามพ่อเองโดยไม่ต้องคิด D ของพ่อซ้ำ (ข้อศอกตามไหล่ไปเอง)
 */

import { Quaternion } from "@babylonjs/core/Maths/math.vector";
import type { TransformNode } from "@babylonjs/core/Meshes/transformNode";
import type { Node } from "@babylonjs/core/node";

type Q = [number, number, number, number]; // x y z w

const qmul = (a: Q, b: Q): Q => [
  a[3] * b[0] + a[0] * b[3] + a[1] * b[2] - a[2] * b[1],
  a[3] * b[1] - a[0] * b[2] + a[1] * b[3] + a[2] * b[0],
  a[3] * b[2] + a[0] * b[1] - a[1] * b[0] + a[2] * b[3],
  a[3] * b[3] - a[0] * b[0] - a[1] * b[1] - a[2] * b[2],
];
const qinv = (a: Q): Q => [-a[0], -a[1], -a[2], a[3]];
const axis = (x: number, y: number, z: number, ang: number): Q => {
  const s = Math.sin(ang / 2);
  return [x * s, y * s, z * s, Math.cos(ang / 2)];
};
const rx = (a: number): Q => axis(1, 0, 0, a);
const ry = (a: number): Q => axis(0, 1, 0, a);
const rz = (a: number): Q => axis(0, 0, 1, a);
const ID: Q = [0, 0, 0, 1];

interface RigBone {
  node: TransformNode;
  /** inv(RW_parent) */
  parentInv: Q;
  /** RW_self */
  self: Q;
}

export interface CharacterRig {
  bones: Map<string, RigBone>;
  /** มุมที่หมุนแขนลงจากท่าตั้งต้นให้แนบตัว (ท่าตั้งต้นของแต่ละไฟล์ไม่เหมือนกัน) */
  lowerR: number;
  lowerL: number;
}

const PREFIX = "mixamorig:";

function toQ(n: TransformNode): Q {
  const q = n.rotationQuaternion ?? Quaternion.FromEulerVector(n.rotation);
  return [q.x, q.y, q.z, q.w];
}

/** หา node กระดูกใต้ `top` แล้วจำท่าตั้งต้นไว้ คืน undefined ถ้าไฟล์ไม่มีกระดูกแบบ Mixamo */
export function buildRig(top: Node): CharacterRig | undefined {
  const bones = new Map<string, RigBone>();
  const nodes = top.getDescendants(false).filter((n) => n.name.includes(PREFIX)) as TransformNode[];
  if (nodes.length === 0) return undefined;

  const world = (n: Node | null): Q => {
    // สะสมจากรากของกระดูก ไม่นับ __root__ (ตัวแปลงแกนของตัวโหลด) และ node ที่อยู่เหนือมัน
    let q: Q = ID;
    for (let cur = n; cur && cur !== top; cur = cur.parent) q = qmul(toQ(cur as TransformNode), q);
    return q;
  };

  for (const node of nodes) {
    node.rotationQuaternion ??= Quaternion.FromEulerVector(node.rotation);
    const name = node.name.slice(node.name.indexOf(PREFIX) + PREFIX.length);
    bones.set(name, { node, parentInv: qinv(world(node.parent)), self: world(node) });
  }

  // แขนตั้งต้นอาจกางข้าง (T-pose) หรือห้อยอยู่แล้ว วัดจากทิศของกระดูกต้นแขนในพื้นที่โมเดล
  const lower = (side: "Right" | "Left"): number => {
    const arm = bones.get(`${side}Arm`)?.node;
    const fore = bones.get(`${side}ForeArm`)?.node;
    if (!arm || !fore) return 0;
    const p = fore.position;
    // ทิศปลายแขนในพื้นที่ของกระดูกต้นแขน → หมุนด้วย RW_arm ไปเป็นพื้นที่โมเดล
    const rw = bones.get(`${side}Arm`)!.self;
    const v = qmul(qmul(rw, [p.x, p.y, p.z, 0]), qinv(rw));
    const horiz = Math.hypot(v[0], v[2]);
    const elev = Math.atan2(v[1], horiz); // 0 = กางระนาบ (T-pose), ≈ -0.8 = A-pose, -π/2 = ห้อยลง
    // ลดลงจนเหลือกางจากลำตัวเล็กน้อย (0.18 rad) ไม่ให้แขนจมเข้าเอว แต่ไม่กางจนดูเหมือนยกแขน
    return Math.max(0, elev + Math.PI / 2 - 0.18);
  };

  return { bones, lowerR: lower("Right"), lowerL: lower("Left") };
}

function set(rig: CharacterRig, name: string, d: Q): void {
  const b = rig.bones.get(name);
  if (!b) return;
  const q = qmul(qmul(b.parentInv, d), b.self);
  b.node.rotationQuaternion!.set(q[0], q[1], q[2], q[3]);
}

/**
 * ใส่ท่าเดิน/ยืน/โจมตี/ร่ายบัฟ/ร่ายเวท ลงกระดูก พารามิเตอร์ตรงกับ `animateChibi`
 *
 * ทิศในพื้นที่ glTF: ขวาของตัวละคร = -X · ไปข้างหน้า = +Z
 * แขนขวา (ชี้ -X) หมุนรอบ +Z ด้วยมุมบวก = ลดลง · แขนซ้ายตรงข้าม
 * ขา/แขนที่ห้อยลง หมุนรอบ X ด้วยมุมลบ = แกว่งไปข้างหน้า
 */
export function animateRig(
  rig: CharacterRig,
  moving: boolean,
  t: number,
  swing = 0,
  weaponType = "sword_1h",
  actionType: "attack" | "buff" | "spell" = "attack",
): void {
  const sw = moving ? Math.sin(t * 12) : 0;
  const arc = swing > 0 ? Math.sin(swing * Math.PI) : 0;

  // ก้าวขาซ้าย-ขวาอย่างเป็นธรรมชาติ
  const cw = moving ? Math.cos(t * 12) : 0;
  set(rig, "RightUpLeg", rx(-sw * 0.42));
  set(rig, "LeftUpLeg", rx(sw * 0.42));
  set(rig, "RightLeg", rx(Math.max(0, cw) * 0.45 + Math.max(0, -sw) * 0.1));
  set(rig, "LeftLeg", rx(Math.max(0, -cw) * 0.45 + Math.max(0, sw) * 0.1));

  // ท่ายืนแกว่งแขนเบา ๆ และหายใจ
  const idleSway = moving ? 0 : Math.sin(t * 2.2) * 0.04;
  let armR = qmul(rx(sw * 0.35), rz(rig.lowerR + idleSway));
  let foreR: Q = rx(moving ? -0.25 : -0.15);
  let armL = qmul(rx(-sw * 0.35), rz(-(rig.lowerL + idleSway)));
  let foreL: Q = rx(moving ? -0.25 : -0.15);
  let spine: Q = moving ? ry(sw * 0.08) : ID;
  let head: Q = ry(moving ? 0 : Math.sin(t * 0.7) * 0.15);

  if (arc > 0) {
    if (actionType === "buff") {
      // ==========================================
      // ท่าร่ายบัฟ (Buff / Support / Aura Prayer)
      // ==========================================
      if (weaponType.startsWith("lantern") || weaponType.startsWith("staff")) {
        // เมนเดอร์/เซนต์/อาร์คานิสต์: ชูสองมือขึ้นสู่ฟ้าเปล่งออร่าศักดิ์สิทธิ์
        armR = qmul(rx(-2.7 * arc), rz(rig.lowerR * 0.3 + 0.35 * arc));
        foreR = rx(-0.4 * arc);
        armL = qmul(rx(-2.5 * arc), rz(-rig.lowerL * 0.3 - 0.35 * arc));
        foreL = rx(-0.4 * arc);
        spine = qmul(rx(-0.2 * arc), ry(0));
        head = qmul(rx(-0.35 * arc), ry(0));
      } else {
        // สายประชิด (วอร์เดน/บรอว์เลอร์/เชด/เรนเจอร์): ยกสองมือกำหมัดระดับอก เบ่งพลังปลดปล่อยออร่า
        armR = qmul(rx(-1.6 * arc), rz(rig.lowerR * 0.4 + 0.25 * arc));
        foreR = rx(-1.4 * arc);
        armL = qmul(rx(-1.6 * arc), rz(-rig.lowerL * 0.4 - 0.25 * arc));
        foreL = rx(-1.4 * arc);
        spine = rx(0.18 * arc);
        head = rx(-0.15 * arc);
      }
    } else if (actionType === "spell") {
      // ==========================================
      // ท่าร่ายเวทมนตร์ (Spell Casting & Blast)
      // ==========================================
      // มือขวายื่นคทา/มือชี้ไปข้างหน้า มือซ้ายวาดวงแหวนเวทนำวิถีแล้วผลักออกไป
      armR = qmul(rx(-1.75 * arc), rz(rig.lowerR * 0.4));
      foreR = rx(-0.3 * arc);
      armL = qmul(rx(-1.65 * arc), rz(-rig.lowerL * 0.4 - 0.25 * arc));
      foreL = rx(-0.95 * arc);
      spine = qmul(ry(-0.32 * arc), rx(0.14 * arc));
      head = ry(-0.1 * arc);
    } else {
      // ==========================================
      // ท่าโจมตีปกติและสกิลโจมตีตามชนิดอาวุธ
      // ==========================================
      if (weaponType.startsWith("knuckle")) {
        // ---- สนับมือ / หมัด (บรอว์เลอร์ / เบอร์เซิร์กเกอร์): ต่อย 2 มือสลับ 1-2 คอมโบ ----
        if (swing < 0.5) {
          // ครึ่งแรก: หมัดซ้ายแย็บพุ่งไปข้างหน้า มือขวาตั้งการ์ดคุมหน้า
          const p1 = Math.sin(swing * 2 * Math.PI);
          armL = qmul(rx(-1.65 * p1), rz(-rig.lowerL * (1 - p1 * 0.75)));
          foreL = rx(-0.25 * (1 - p1) - 0.08 * p1);
          armR = qmul(rx(-1.35), rz(rig.lowerR * 0.35));
          foreR = rx(-1.5);
          spine = qmul(ry(0.42 * p1), rx(0.1 * p1));
        } else {
          // ครึ่งหลัง: หมัดขวาตรงกระแทกเต็มเหนี่ยว มือซ้ายดึงกลับมาตั้งการ์ด
          const p2 = Math.sin((swing - 0.5) * 2 * Math.PI);
          armR = qmul(rx(-1.7 * p2), rz(rig.lowerR * (1 - p2 * 0.75)));
          foreR = rx(-0.25 * (1 - p2) - 0.08 * p2);
          armL = qmul(rx(-1.35), rz(-rig.lowerL * 0.35));
          foreL = rx(-1.5);
          spine = qmul(ry(-0.45 * p2), rx(0.18 * p2));
        }
      } else if (weaponType.startsWith("dagger")) {
        // ---- กริช / มีดสั้น (เชด / แอสแซสซิน): กวัดแกว่งแทงเฉือน 2 มือ Dual Dagger Flurry ----
        if (swing < 0.45) {
          // จังหวะ 1: มือขวาพุ่งแทงทะลวงไปข้างหน้า มือซ้ายเงื้อใบมีดพร้อมตวัด
          const d1 = Math.sin((swing / 0.45) * Math.PI);
          armR = qmul(rx(-1.65 * d1), rz(rig.lowerR * 0.5));
          foreR = rx(-0.5 * d1);
          armL = qmul(rx(-0.85 * d1), rz(-rig.lowerL - 0.35 * d1));
          foreL = rx(-1.1 * d1);
          spine = qmul(ry(-0.35 * d1), rx(0.15 * d1));
        } else {
          // จังหวะ 2: มือซ้ายตวัดฟันไขว้กากบาท (X-Cross Slash) มือขวาดึงกลับมาคุมจังหวะ
          const d2 = Math.sin(((swing - 0.45) / 0.55) * Math.PI);
          armL = qmul(rx(-1.55 * d2), rz(-rig.lowerL * 0.3 - 0.4 * d2));
          foreL = rx(-0.85 * d2);
          armR = qmul(rx(-1.05 * d2), rz(rig.lowerR * 0.6));
          foreR = rx(-1.2 * d2);
          spine = qmul(ry(0.38 * d2), rx(0.15 * d2));
        }
      } else if (weaponType.startsWith("bow") || weaponType.startsWith("crossbow")) {
        // ---- ธนู / หน้าไม้: แขนซ้ายเล็งไปข้างหน้า แขนขวาง้างสายธนูมาข้างแก้มแล้วปล่อย ----
        const draw = swing < 0.6 ? swing / 0.6 : 1 - (swing - 0.6) / 0.4;
        armL = qmul(rx(-1.55), rz(-rig.lowerL * 0.25));
        foreL = rx(-0.1);
        armR = qmul(rx(-1.35 + 0.3 * draw), rz(rig.lowerR * 0.45));
        foreR = rx(-1.8 * draw);
        spine = ry(0.35 * arc);
      } else if (weaponType.startsWith("staff")) {
        // ---- คทา: ชูคทาขึ้นสูงเหนือหัวแล้วฟาดปล่อยคลื่นพลัง ----
        const lift = swing < 0.4 ? swing / 0.4 : 1 - (swing - 0.4) / 0.6;
        armR = qmul(rx(-2.6 * lift), rz(rig.lowerR * 0.45));
        foreR = rx(-0.5 * lift);
        armL = qmul(rx(-1.25 * arc), rz(-rig.lowerL * 0.5));
        foreL = rx(-0.85 * arc);
        spine = qmul(rx(0.16 * arc), ry(-0.2 * arc));
      } else if (weaponType.startsWith("lantern")) {
        // ---- ตะเกียงวิญญาณ: ชูตะเกียงส่องสว่าง มือซ้ายประสานพลังศักดิ์สิทธิ์ ----
        armR = qmul(rx(-2.15 * arc), rz(rig.lowerR * 0.55));
        foreR = rx(-0.4 * arc);
        armL = qmul(rx(-1.45 * arc), rz(-rig.lowerL * 0.5));
        foreL = rx(-0.9 * arc);
        spine = rx(-0.1 * arc);
      } else if (weaponType.startsWith("greatsword")) {
        // ---- ดาบใหญ่ 2 มือ: สองมือจับดาบใหญ่ ยกขึ้นเหนือไหล่แล้วฟันเต็มแรง ----
        const lift = swing < 0.5 ? swing / 0.5 : 1 - (swing - 0.5) / 0.5;
        armR = qmul(rx(-2.6 * lift - 0.4 * arc), rz(rig.lowerR * 0.35));
        foreR = rx(-0.6 * lift);
        armL = qmul(rx(-2.3 * lift - 0.3 * arc), rz(-rig.lowerL * 0.2));
        foreL = rx(-1.1 * lift);
        spine = qmul(ry(-0.45 * arc), rx(0.25 * arc));
      } else {
        // ---- ดาบมือเดียว / ดาบและโล่: มือซ้ายยกโล่ป้องกัน มือขวาฟันดาบเฉียงลง ----
        const lift = swing < 0.45 ? swing / 0.45 : 1 - (swing - 0.45) / 0.55;
        armR = qmul(rx(-2.4 * lift - 0.4 * arc), rz(rig.lowerR * 0.75));
        foreR = rx(-0.6 * lift);
        armL = qmul(rx(-1.35 * arc), rz(-rig.lowerL * 0.35));
        foreL = rx(-1.2 * arc);
        spine = qmul(ry(-0.35 * arc), rx(0.18 * arc));
      }
    }
  }

  set(rig, "RightArm", armR);
  set(rig, "RightForeArm", foreR);
  set(rig, "LeftArm", armL);
  set(rig, "LeftForeArm", foreL);
  set(rig, "Spine1", spine);
  set(rig, "Head", head);
}
